'use client';

import type { JSX } from 'react';

import { TOOLTIP_TEXT_CHANCE, TOOLTIP_TEXT_FORECAST, TOOLTIP_TEXT_UNDERLYING } from 'constants/tooltips';
import { NumberChangeAnimationWrapper } from 'DesignSystem2/Animation';
import ProjectionChangeText from 'DesignSystem2/Projection/ProjectionChangeText';
import { Palette } from 'DesignSystem2/Theme';
import Spacing from 'DesignSystem2/Theme/Spacing';
import Emphasis from 'DesignSystem3/Text/Emphasis';
import Headline from 'DesignSystem3/Text/Headline';
import Title from 'DesignSystem3/Text/Title';
import { toVariableLengthFloat } from 'utils/caseing';
import { dynamicWithRetry } from 'utils/dynamicWithRetry';

import { AnimatedProjectionText } from './AnimatedProjectionText';

const ProjectionLabelTooltip = dynamicWithRetry(() => import('./ProjectionLabelTooltip'));

export enum ProjectionType {
    FORECAST = 'forecast',
    CHANCE = 'chance',
    PRICE = 'price',
    UNDERLYING = 'underlying',
}

interface ProjectionLabelProps {
    type: ProjectionType;
    projection: number;
    change: number;
    text?: string;
    customLabel?: string;
    color?: string;
    changeText?: string;
    changeColor?: string;
    hideChange?: boolean;
    dontAnimate?: boolean;
    size?: 'micro' | 'small' | 'medium' | 'large';
    customTooltipText?: string;
    hideTooltip?: boolean;
    numberScrollAnimation?: boolean;
}

const ProjectionLabel = ({
    type,
    projection,
    change,
    text,
    customLabel,
    color,
    changeText,
    changeColor,
    hideChange,
    dontAnimate,
    size,
    customTooltipText,
    hideTooltip = false,
    numberScrollAnimation = true,
}: ProjectionLabelProps): JSX.Element => {
    let projectionText = '';
    let typeLabel = '';
    let tooltipText = '';

    switch (type) {
        case ProjectionType.FORECAST:
            projectionText = text ?? `${projection}`;
            typeLabel = customLabel ?? 'forecast';
            tooltipText = TOOLTIP_TEXT_FORECAST;
            break;
        case ProjectionType.CHANCE:
            projectionText = text ?? `${toVariableLengthFloat(projection, 1)}%`;
            typeLabel = customLabel ?? 'chance';
            tooltipText = TOOLTIP_TEXT_CHANCE;
            break;
        case ProjectionType.PRICE:
            projectionText = text ?? `${projection}¢`;
            typeLabel = customLabel ?? 'last traded';
            tooltipText = TOOLTIP_TEXT_CHANCE;
            break;
        case ProjectionType.UNDERLYING:
            projectionText = text ?? `${projection}`;
            typeLabel = customLabel ?? '';
            tooltipText = TOOLTIP_TEXT_UNDERLYING;
            break;
    }

    if (!!customTooltipText) {
        tooltipText = customTooltipText;
    }

    if (size === 'micro') {
        return (
            <div className="bg-fill-white flex gap-1 items-center px-[0.75rem] py-[0.25rem] border border-solid border-fill-x40 rounded-x50">
                <Title.x20>
                    <AnimatedProjectionText
                        text={projectionText}
                        fontSize="18px"
                        numberScrollAnimation={numberScrollAnimation}
                    />
                </Title.x20>
                {!change ? '' : change > 0 ? '↑' : '↓'}
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: '6px',
            }}
        >
            <NumberChangeAnimationWrapper
                number={projection}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                }}
                animate={!dontAnimate}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Spacing(0.5),
                        color: color || (!!change && change < 0 ? Palette.Critical.x50 : Palette.Green.x50),
                        minHeight: size === 'large' ? 28 : undefined,
                    }}
                >
                    {!!projectionText && (
                        <Headline.x10>
                            <AnimatedProjectionText
                                text={projectionText}
                                fontSize="24px"
                                numberScrollAnimation={numberScrollAnimation}
                            />
                        </Headline.x10>
                    )}
                    {!hideChange && <Emphasis.x20>{typeLabel}</Emphasis.x20>}
                </div>
                {!hideChange && !!change && (
                    <div className="flex ml-1">
                        <ProjectionChangeText
                            change={change}
                            text={changeText}
                            color={changeColor}
                            numberScrollAnimation={numberScrollAnimation}
                            fixedLength={!!dontAnimate}
                        />
                    </div>
                )}
            </NumberChangeAnimationWrapper>
            {!hideTooltip && <ProjectionLabelTooltip tooltipText={tooltipText} />}
        </div>
    );
};

export default ProjectionLabel;
